import React, { useEffect, useState } from "react";
import { hubExt } from "../api.js";
import { toast } from "react-toastify";
import { useIsMobile } from "../hooks/useIsMobile.js";

const TAG_OPTS = [
  { value: "new",     label: "Nou",            color: "#7c3aed", bg: "#eff6ff" },
  { value: "feature", label: "Funcționalitate", color: "#16a34a", bg: "#f0fdf4" },
  { value: "fix",     label: "Corecție",        color: "#c2410c", bg: "#fff7ed" },
  { value: "info",    label: "Info",             color: "#64748b", bg: "#f8fafc" },
];

const EMPTY = { title: "", body: "", tag: "info", version: "", published: true };

export default function UpdateNotesPage() {
  const isMobile = useIsMobile();
  const [notes, setNotes]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState(EMPTY);
  const [editing, setEditing]   = useState(null); // _id being edited
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    try {
      const { data } = await hubExt.getUpdateNotes();
      setNotes(data.notes || []);
    } catch { toast.error("Eroare la încărcare."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm(EMPTY); setEditing(null); };

  const handleEdit = (note) => {
    setEditing(note._id);
    setForm({ title: note.title, body: note.body, tag: note.tag, published: note.published });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return toast.error("Titlu și conținut obligatorii.");
    setSaving(true);
    try {
      if (editing) {
        await hubExt.updateUpdateNote(editing, form);
        toast.success("Notă actualizată.");
      } else {
        await hubExt.createUpdateNote(form);
        toast.success("Notă publicată.");
      }
      resetForm();
      await load();
    } catch { toast.error("Eroare la salvare."); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Ștergi această notă?")) return;
    setDeleting(id);
    try {
      await hubExt.deleteUpdateNote(id);
      toast.success("Notă ștearsă.");
      await load();
    } catch { toast.error("Eroare la ștergere."); }
    finally { setDeleting(null); }
  };

  const tagStyle = (tag) => TAG_OPTS.find(t => t.value === tag) || TAG_OPTS[3];

  return (
    <div style={{ padding: isMobile ? "16px" : "32px 36px", maxWidth: 900, margin: "0 auto" }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.4px" }}>Noutăți / Update notes</h1>
        <p style={{ fontSize: 14, color: "#64748b", marginTop: 4 }}>Publicate live pe app.orderly.ro pentru toți utilizatorii</p>
      </div>

      {/* Form */}
      <div style={{ background: "white", borderRadius: 16, border: "1.5px solid #e2e8f0", padding: 28, marginBottom: 28 }}>
        <h2 style={{ fontSize: 15, fontWeight: 800, color: "#0f172a", marginBottom: 20 }}>
          {editing ? "Editează nota" : "Adaugă notă nouă"}
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Titlu</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Ex: Funcționalitate nouă de plată split" required style={inputStyle} />
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Tag</label>
                <select value={form.tag} onChange={e => setForm(f => ({ ...f, tag: e.target.value }))} style={inputStyle}>
                  {TAG_OPTS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div style={{ width: 120 }}>
                <label style={labelStyle}>Versiune</label>
                <input value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))}
                  placeholder="v2.4.1" style={inputStyle}/>
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8, height: 44, paddingTop: 4 }}>
                  <input type="checkbox" id="pub-check" checked={form.published} onChange={e => setForm(f => ({ ...f, published: e.target.checked }))} style={{ width: 16, height: 16, accentColor: "#1d4ed8", cursor: "pointer" }} />
                  <label htmlFor="pub-check" style={{ fontSize: 13, fontWeight: 600, color: "#475569", cursor: "pointer" }}>Publicat</label>
                </div>
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Conținut</label>
            <textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
              placeholder="Descrie pe scurt ce s-a schimbat sau ce e nou..." required rows={4}
              style={{ ...inputStyle, height: "auto", padding: "12px 14px", resize: "vertical" }} />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="submit" disabled={saving} style={btnPrimary}>{saving ? "Se salvează..." : editing ? "Actualizează nota" : "Publică nota"}</button>
            {editing && <button type="button" onClick={resetForm} style={btnGhost}>Anulează</button>}
          </div>
        </form>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: "center", color: "#94a3b8", padding: 40 }}>Se încarcă...</div>
      ) : notes.length === 0 ? (
        <div style={{ textAlign: "center", color: "#94a3b8", padding: 48, background: "white", borderRadius: 14, border: "1px solid #f1f5f9" }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
          <div style={{ fontWeight: 700, color: "#475569", marginBottom: 4 }}>Nicio notă publicată</div>
          <div style={{ fontSize: 13 }}>Adaugă prima notă pentru utilizatori.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {notes.map(note => {
            const ts = tagStyle(note.tag);
            return (
              <div key={note._id} style={{ background: "white", borderRadius: 12, border: "1.5px solid #f1f5f9", padding: "18px 20px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 100, background: ts.bg, color: ts.color, textTransform: "uppercase", letterSpacing: ".4px" }}>{ts.label}</span>
                      {note.version && <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 100, background: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0", fontFamily: "monospace" }}>{note.version}</span>}
                      {!note.published && <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 100, background: "#f8fafc", color: "#94a3b8", border: "1px solid #e2e8f0" }}>Draft</span>}
                      <span style={{ fontSize: 12, color: "#94a3b8" }}>{new Date(note.createdAt).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" })}</span>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>{note.title}</div>
                    <div style={{ fontSize: 14, color: "#64748b", lineHeight: 1.6 }}>{note.body}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button onClick={() => handleEdit(note)} style={{ ...btnSmall, background: "#eff6ff", color: "#7c3aed", border: "1px solid #ddd6fe" }}>Editează</button>
                    <button onClick={() => handleDelete(note._id)} disabled={deleting === note._id} style={{ ...btnSmall, background: "#fff1f2", color: "#e11d48", border: "1px solid #fecdd3" }}>
                      {deleting === note._id ? "..." : "Șterge"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const labelStyle = { fontSize: 13, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 };
const inputStyle = { width: "100%", height: 44, border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "0 14px", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", background: "white", color: "#0f172a" };
const btnPrimary = { height: 44, padding: "0 24px", background: "#1d4ed8", color: "white", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" };
const btnGhost = { height: 44, padding: "0 20px", background: "white", color: "#64748b", border: "1.5px solid #e2e8f0", borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" };
const btnSmall = { padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };
